import React, {
  HTMLAttributes,
  ReactNode,
  RefCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { st, classes } from './SlidingModal.st.css.js';

export interface SlidingModalProps {
  dataHook?: string;
  width: string | number;
  isOpen?: boolean;
  style?: HTMLAttributes<HTMLElement>['style'];
  className?: HTMLAttributes<HTMLElement>['className'];
  children?: ReactNode;
  contentRef?: RefCallback<HTMLDivElement>;
  onExited?: () => void;
  onEntered?: () => void;
  transitionStatus?: string;
  onRequestClose?: () => void;
  shadowWidth?: number;
}

export function SlidingModal(props: SlidingModalProps) {
  const {
    dataHook,
    className,
    style: styleProp,
    shadowWidth = 20,
    width,
    isOpen,
    children,
    contentRef,
    onExited,
    onEntered,
    onRequestClose,
    transitionStatus,
  } = props;

  const style = useMemo(
    () => ({
      ...styleProp,
      transform: `translate3d(${
        isOpen
          ? 0
          : typeof width === 'number'
          ? `${width + shadowWidth}px`
          : `${width}px`
      }, 0, 0)`,
    }),
    [styleProp, width, isOpen],
  );

  const isOpenRef = useRef(isOpen);
  const divRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (process.env.NODE_ENV === 'test' && isOpen) {
      onEntered?.();
    } else {
      if (process.env.NODE_ENV === 'test' && isOpenRef.current) {
        onExited?.();
      }
    }

    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const fn = (e: TransitionEvent) => {
      if (e.propertyName === 'transform') {
        isOpenRef.current ? onEntered?.() : onExited?.();
      }
    };

    divRef.current?.addEventListener('transitionend', fn);

    return () => {
      divRef.current?.removeEventListener('transitionend', fn);
    };
  }, []);

  return (
    <div
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.nativeEvent.code === 'Escape') {
          onRequestClose?.();
        }
      }}
      ref={(el) => {
        if (el) {
          divRef.current = el;
        }

        contentRef?.(el);
      }}
      data-hook={dataHook}
      data-transition-status={transitionStatus}
      className={st(
        classes.root,
        {},
        ...[className, isOpen ? 'cairo-sliding-modal-is-open' : undefined],
      )}
      role="presentation"
      style={style}
      aria-modal={undefined}
    >
      {children}
    </div>
  );
}
